import React, { useMemo } from "react";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import { useAuth } from "context/AuthProvider";
import { RuleRequired } from "utils/MyUtils";
import { boolText, hasPermission, parseBoolean, showEmpty } from "features/kms/system/SystemPageUtils";

function toOptions(items, valueKey, labelBuilder) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    ...item,
    __value: item?.[valueKey] ? String(item[valueKey]) : "",
    __label: labelBuilder(item),
  }));
}

export default function OrganizationRelationPage() {
  const { user } = useAuth();
  const [organisasiResp] = useAxios({ method: "GET", url: "/references/organisasi", auth: true });
  const [jenisRelasiResp] = useAxios({ method: "GET", url: "/references/jenis-relasi-organisasi", auth: true });
  const [, , , axiosFetch] = useAxiosFunction();

  const organisasi = Array.isArray(organisasiResp?.data) ? organisasiResp.data : [];
  const jenisRelasi = Array.isArray(jenisRelasiResp?.data) ? jenisRelasiResp.data : [];
  const canManage = hasPermission(user, "org.manage");

  const organisasiOptions = useMemo(
    () =>
      toOptions(organisasi, "id_organisasi", (item) => item?.nama || ""),
    [organisasi]
  );

  const relasiOptions = useMemo(
    () => toOptions(jenisRelasi, "id_jenis_relasi_organisasi", (item) => item?.nama || ""),
    [jenisRelasi]
  );

  const coltype = useMemo(
    () => ({
      id_relasi_organisasi: { type: "hidden", name: "id_relasi_organisasi" },
      id_organisasi: {
        label: "Organisasi",
        type: "dropdown",
        name: "id_organisasi",
        colsize: 6,
        validation: RuleRequired,
        options: organisasiOptions,
        valueId: "__value",
        labelId: "__label",
        placeholder: "-Pilih Organisasi-",
      },
      id_organisasi_terkait: {
        label: "Organisasi Terkait",
        type: "dropdown",
        name: "id_organisasi_terkait",
        colsize: 6,
        validation: RuleRequired,
        options: organisasiOptions,
        valueId: "__value",
        labelId: "__label",
        placeholder: "-Pilih Organisasi Terkait-",
      },
      id_jenis_relasi_organisasi: {
        label: "Jenis Relasi",
        type: "dropdown",
        name: "id_jenis_relasi_organisasi",
        colsize: 6,
        validation: RuleRequired,
        options: relasiOptions,
        valueId: "__value",
        labelId: "__label",
        placeholder: "-Pilih Jenis Relasi-",
      },
      tanggal_mulai: {
        label: "Tanggal Mulai",
        type: "date",
        name: "tanggal_mulai",
        colsize: 3,
        width: 110,
        renderFunc: (val) => showEmpty(val),
      },
      tanggal_selesai: {
        label: "Tanggal Selesai",
        type: "date",
        name: "tanggal_selesai",
        colsize: 3,
        width: 110,
        renderFunc: (val) => showEmpty(val),
      },
      aktif: {
        label: "Status",
        type: "checkbox",
        name: "aktif",
        colsize: 3,
        width: 100,
        align: "center",
        renderFunc: (val) => boolText(val),
      },
      organisasi_name: { label: "Organisasi", renderFunc: (val) => showEmpty(val), width: 220 },
      organisasi_terkait_name: { label: "Organisasi Terkait", renderFunc: (val) => showEmpty(val), width: 220 },
      jenis_relasi_name: { label: "Jenis Relasi", renderFunc: (val) => showEmpty(val), width: 180 },
      action: { label: "Aksi", align: "center", width: 150 },
    }),
    [organisasiOptions, relasiOptions]
  );

  const saveHandle = async (form) => {
    const id = form?.id_relasi_organisasi || "";
    const method = id ? "PUT" : "POST";
    const url = id ? `/organisasi-relasi/${id}` : "/organisasi-relasi";

    return axiosFetch({
      method,
      url,
      auth: true,
      data: {
        id_organisasi: form?.id_organisasi || "",
        id_organisasi_terkait: form?.id_organisasi_terkait || "",
        id_jenis_relasi_organisasi: Number(form?.id_jenis_relasi_organisasi || 0),
        tanggal_mulai: form?.tanggal_mulai || "",
        tanggal_selesai: form?.tanggal_selesai || "",
        aktif: Boolean(form?.aktif),
      },
    });
  };

  return (
    <>
      <DataList
        title="Relasi Organisasi"
        coltype={coltype}
        dlist={{
          url: "/organisasi-relasi",
          columns: ["no", "organisasi_name", "jenis_relasi_name", "organisasi_terkait_name", "tanggal_mulai", "tanggal_selesai", "aktif", "action"],
          filter: [{ dtype: "q", name: "q", colsize: 4 }],
          sort_by: ["organisasi_name", "jenis_relasi_name", "organisasi_terkait_name"],
        }}
        addconfig={canManage ? {
          columns: ["id_organisasi", "id_organisasi_terkait", "id_jenis_relasi_organisasi", "tanggal_mulai", "tanggal_selesai", "aktif"],
          title: "Tambah Relasi Organisasi",
          label_tool: "Tambah Relasi",
          defaultValue: { aktif: true },
          saveHandle,
        } : null}
        editconfig={canManage ? {
          columns: ["id_relasi_organisasi", "id_organisasi", "id_organisasi_terkait", "id_jenis_relasi_organisasi", "tanggal_mulai", "tanggal_selesai", "aktif"],
          title: "Ubah Relasi Organisasi",
          url: "/organisasi-relasi",
          urlitem: "/organisasi-relasi",
          cellid: "id_relasi_organisasi",
          saveHandle,
          normalizeItem: (item) => ({
            ...item,
            id_organisasi: item?.id_organisasi ? String(item.id_organisasi) : "",
            id_organisasi_terkait: item?.id_organisasi_terkait ? String(item.id_organisasi_terkait) : "",
            id_jenis_relasi_organisasi: item?.id_jenis_relasi_organisasi ? String(item.id_jenis_relasi_organisasi) : "",
            aktif: parseBoolean(item?.aktif),
          }),
        } : null}
        delconfig={canManage ? {
          url: "/organisasi-relasi",
          cellid: "id_relasi_organisasi",
          cellname: "jenis_relasi_name",
        } : null}
        activateconfig={canManage ? {
          url: "/organisasi-relasi",
          cellid: "id_relasi_organisasi",
          cellname: "jenis_relasi_name",
          cellactivate: "aktif",
          labelactivate: "Aktifkan",
          labelinactivate: "Non-aktifkan",
          cfmactivate: "mengaktifkan",
          cfminactivate: "menonaktifkan",
          infoactivate: "diaktifkan",
          infoinactivate: "dinonaktifkan",
        } : null}
      />
    </>
  );
}
